import { useCallback, useEffect, useState } from "react";
import { ShieldAlert, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/** "Was this you?" — shown on a trusted device while an unknown device waits at sign-in. */
export function DeviceApprovalCard() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get("/auth/device-approvals").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => {
    const onCount = (e) => { if (e.detail > 0) load(); else setItems([]); };
    window.addEventListener("kingly:approvals", onCount);
    return () => window.removeEventListener("kingly:approvals", onCount);
  }, [load]);
  if (items.length === 0) return null;

  const decide = async (a, yes) => {
    setBusy(a.id);
    try {
      await api.post(`/auth/device-approvals/${a.id}/${yes ? "approve" : "deny"}`);
      toast.success(yes ? "Approved — that device is signing in now" : "Blocked. That device can't sign in");
      setItems((c) => c.filter((x) => x.id !== a.id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update");
    } finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-x-3 bottom-20 sm:bottom-6 sm:right-6 sm:left-auto sm:w-[380px] z-[90] space-y-2" data-testid="device-approval-card">
      {items.slice(0, 3).map((a) => (
        <div key={a.id} className="rounded-2xl border border-amber-300 bg-white shadow-2xl p-4" data-testid={`device-approval-${a.id}`}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center shrink-0"><ShieldAlert className="w-5 h-5" /></div>
            <div className="min-w-0">
              <div className="font-semibold text-slate-900">Was this you?</div>
              <p className="text-sm text-slate-600 mt-0.5">
                Someone is signing in to your Kingly account on <b>{a.device || "a new device"}</b>{a.place ? ` near ${a.place}` : ""}{a.net ? ` through a ${a.net === "hosting" ? "datacenter" : a.net.toUpperCase()} connection` : ""}.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <button type="button" disabled={busy === a.id} onClick={() => decide(a, false)}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 text-rose-800 py-2 text-sm font-medium hover:bg-rose-100 disabled:opacity-50" data-testid={`device-approval-deny-${a.id}`}>
              <X className="w-4 h-4" /> No, block it
            </button>
            <button type="button" disabled={busy === a.id} onClick={() => decide(a, true)}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-slate-900 text-white py-2 text-sm font-medium hover:bg-slate-800 disabled:opacity-50" data-testid={`device-approval-approve-${a.id}`}>
              <Check className="w-4 h-4" /> Yes, that's me
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
